import React, { useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import AnimatedText from './AnimatedText';

const projects = [
  {
    id: 1,
    title: 'Luxe Fashion Launch',
    category: 'Brand Building',
    year: '2025',
    stat: '2.5M reach',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop&q=70',
  },
  {
    id: 2,
    title: 'Tech Startup Social',
    category: 'Social Media Marketing',
    year: '2025',
    stat: '+300% sign-ups',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=70',
  },
  {
    id: 3,
    title: 'Restaurant Chain UGC',
    category: 'UGC Creation',
    year: '2024',
    stat: '+40% foot traffic',
    image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&auto=format&fit=crop&q=70',
  },
  {
    id: 4,
    title: 'E-commerce Redesign',
    category: 'Website Development',
    year: '2024',
    stat: '+85% conversions',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=70',
  },
];

const Portfolio = () => {
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { damping: 22, stiffness: 220, mass: 0.4 });
  const sy = useSpring(my, { damping: 22, stiffness: 220, mass: 0.4 });

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mx.set(e.clientX - rect.left);
    my.set(e.clientY - rect.top);
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-28 sm:py-40 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <p className="lg:col-span-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#AA94FF]">
            Selected work
          </p>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[0.98] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={50}
            >
              Recent projects, real numbers.
            </AnimatedText>
          </div>
        </div>

        {/* Hover-preview list */}
        <div
          ref={containerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHovered(null)}
          className="relative border-t border-[#1C1917]/15"
        >
          {projects.map((p, i) => (
            <motion.a
              key={p.id}
              data-testid={`portfolio-row-${p.id}`}
              data-cursor-label="View"
              href="#contact"
              onMouseEnter={() => setHovered(p.id)}
              className="group relative flex items-center justify-between gap-6 py-8 sm:py-10 border-b border-[#1C1917]/15 hover:pl-6 transition-all duration-500"
            >
              <motion.span
                className="absolute left-0 top-0 bottom-0 w-0 bg-[#1C1917]/[0.03] pointer-events-none"
                animate={{ width: hovered === p.id ? '100%' : '0%' }}
                transition={{ duration: 0.5, ease: [0.2, 0.65, 0.3, 0.9] }}
              />

              <div className="relative flex items-baseline gap-6 min-w-0">
                <span
                  className="text-sm text-[#78716C] font-mono w-10 shrink-0"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  0{i + 1}
                </span>
                <span
                  className={`text-3xl sm:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1] transition-colors duration-500 ${
                    hovered === p.id ? 'text-[#AA94FF]' : 'text-[#1C1917]'
                  }`}
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {p.title}
                </span>
              </div>

              <div className="relative hidden md:flex items-baseline gap-8 shrink-0 pr-4">
                <span className="text-xs uppercase tracking-[0.15em] text-[#78716C] font-semibold">
                  {p.category}
                </span>
                <span className="text-xs uppercase tracking-[0.15em] text-[#78716C]">
                  {p.year}
                </span>
                <span className="text-sm font-bold text-[#AA94FF]">{p.stat}</span>
              </div>

              <motion.span
                animate={{
                  rotate: hovered === p.id ? 0 : -45,
                  scale: hovered === p.id ? 1.1 : 1,
                }}
                transition={{ type: 'spring', damping: 14, stiffness: 220 }}
                className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  hovered === p.id ? 'bg-[#AA94FF] text-[#F4F4F3]' : 'bg-transparent text-[#1C1917]'
                }`}
              >
                <ArrowUpRight className="w-5 h-5" />
              </motion.span>
            </motion.a>
          ))}

          {/* Cursor-following image preview */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                key={hovered}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{
                  x: sx,
                  y: sy,
                  translateX: '-50%',
                  translateY: '-50%',
                }}
                className="pointer-events-none absolute top-0 left-0 w-[280px] h-[360px] sm:w-[360px] sm:h-[440px] rounded-2xl overflow-hidden shadow-2xl z-10 hidden md:block"
              >
                <img
                  src={projects.find((x) => x.id === hovered)?.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/30 via-transparent to-transparent" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
